import {
    AnnualGiftingStrategy,
    EstimatedImpact,
    GiftToGrantorTrustEstimatedImpact,
    GiftToGrantorTrustStrategy,
    GRATEstimatedImpactRequestDTO,
    GRATRates,
    GRATStrategy,
    PayoutPercentagesRequestDTO,
    PayoutPercentagesResponseDTO,
    SOREstimatedImpact,
    StateEstateTransferRates,
    StateOfResidencyStrategy,
    StrategyDetailsSectionInfo,
    WealthPOSummaryResponse
} from "./WealthPOTypes";
import {get, HttpOptions, post, put, remove} from "../core/api"

const extractBody = async (res: Response) => {
    if (!res.text) {
        return res.json();
    }
    const body = await res.text();
    return JSON.parse(body);
}

const extractNullableBody = async (res: Response) => {
    if(res.status === 204 || res.headers?.get?.("Content-Length") === "0"){
        return null;
    }
    if (!res.text) {
        return res.json();
    }
    const body = await res.text();
    return body ? JSON.parse(body) : null;
}
const isSuccess = (res: Response) => res.status === 200 || res.status === 201;

const getStateEstateTransferRatesReferenceData = (profileId: string): Promise<StateEstateTransferRates> => {
    return get(`/holistic-advice/profiles/${profileId}/wealthpo/strategy/referenceData/stateEstateTransferRates`)
        .then(extractBody);
}

const getGRATRatesReferenceData = (profileId: string, options?: HttpOptions): Promise<GRATRates> => {
    return get(`/holistic-advice/profiles/${profileId}/wealthpo/strategy/referenceData/gratRates`, options)
        .then(extractBody)
}

const getPayoutPercentages = (profileId: string, payoutPercentagesRequestDTO: PayoutPercentagesRequestDTO, options?: HttpOptions): Promise<PayoutPercentagesResponseDTO> => {
    return post(`/holistic-advice/profiles/${profileId}/wealthpo/strategy/grat/payoutPercentages`, payoutPercentagesRequestDTO, options)
        .then(extractBody)
}

const getEstimatedImpactForSOR = (profileId: string, selectedStateOfResidency: string, abortSignal?: AbortSignal): Promise<SOREstimatedImpact> => {
    return get(
        `/holistic-advice/profiles/${profileId}/wealthpo/strategy/stateOfResidency/estimatedImpact/${selectedStateOfResidency}`,
        {signal: abortSignal}
    ).then(extractBody);
}

const getEstimatedImpactForAnnualGifting = (profileId: string, requestBody: AnnualGiftingStrategy, abortSignal?: AbortSignal)
    : Promise<EstimatedImpact> => {
    return post(`/holistic-advice/profiles/${profileId}/wealthpo/strategy/annualGifting/estimatedImpact`,
        requestBody,
        {signal: abortSignal}
    ).then(extractBody);
}

const getEstimatedImpactForGiftToGrantorTrust = (profileId: string, requestBody: GiftToGrantorTrustStrategy, abortSignal?: AbortSignal)
    : Promise<GiftToGrantorTrustEstimatedImpact> => {
    return post(`/holistic-advice/profiles/${profileId}/wealthpo/strategy/giftToGrantorTrust/estimatedImpact`,
        requestBody,
        {signal: abortSignal}
    ).then(extractBody);
}

const getEstimatedImpactForGRAT = (profileId: string, requestBody: GRATEstimatedImpactRequestDTO, options?: HttpOptions): Promise<EstimatedImpact> => {
    return post(`/holistic-advice/profiles/${profileId}/wealthpo/strategy/grat/estimatedImpact`, requestBody, options)
        .then(extractBody);
}

const saveStateOfResidencyStrategy = (profileId: string, requestBody: StateOfResidencyStrategy) => {
    return post(`/holistic-advice/profiles/${profileId}/wealthpo/strategy/stateOfResidency`, requestBody)
        .then(isSuccess);
}

const editStateOfResidencyStrategy = (profileId: string, requestBody: StateOfResidencyStrategy) => {
    return put(`/holistic-advice/profiles/${profileId}/wealthpo/strategy/stateOfResidency`, requestBody)
        .then(isSuccess);
}

const deleteStateOfResidencyStrategy = async (profileId: string, strategyId: string) => {
    const res = await remove(`/holistic-advice/profiles/${profileId}/wealthpo/strategy/stateOfResidency/${strategyId}`);
    return isSuccess(res);
}

const deleteStrategy = (profileId: string, strategyId: string) => {
    return remove(`/holistic-advice/profiles/${profileId}/wealthpo/strategy/delete/${strategyId}`)
        .then(isSuccess)
}

const createAnnualGiftingStrategy = (profileId: string, requestBody: AnnualGiftingStrategy) => {
    return post(`/holistic-advice/profiles/${profileId}/wealthpo/strategy/annualGifting`, requestBody)
        .then(isSuccess);
}

const editAnnualGiftingStrategy = (profileId: string, requestBody: AnnualGiftingStrategy) => {
    return put(`/holistic-advice/profiles/${profileId}/wealthpo/strategy/annualGifting`, requestBody)
        .then(isSuccess);
}

const createGiftToGrantorTrustStrategy = (profileId: string, requestBody: GiftToGrantorTrustStrategy) => {
    return post(`/holistic-advice/profiles/${profileId}/wealthpo/strategy/giftToGrantorTrust`, requestBody)
        .then(isSuccess);
}

const editGiftToGrantorTrustStrategy = (profileId: string, requestBody: GiftToGrantorTrustStrategy) => {
    return put(`/holistic-advice/profiles/${profileId}/wealthpo/strategy/giftToGrantorTrust`, requestBody)
        .then(isSuccess);
}

const createGRATStrategy = (profileId: string, requestBody: GRATStrategy) => {
    return post(`/holistic-advice/profiles/${profileId}/wealthpo/strategy/grat`, requestBody)
        .then(isSuccess);
}

const editGRATStrategy = (profileId: string, requestBody: GRATStrategy) => {
    return put(`/holistic-advice/profiles/${profileId}/wealthpo/strategy/grat`, requestBody)
        .then(isSuccess);
}

const editStrategyEnabledStatus = (profileId: string, requestBody: StrategyDetailsSectionInfo) => {
    return put(`/holistic-advice/profiles/${profileId}/wealthpo/strategy/updateEnabledStatus`, requestBody)
        .then(isSuccess);
}

const getWealthPOSummary = (profileId: string): Promise<WealthPOSummaryResponse | null> => {
    return get(`/holistic-advice/profiles/${profileId}/wealthpo/strategy/summary`)
        .then(extractNullableBody);
}

const getWealthPOSummaryForSelectedState = async (profileId: string, selectedState: string): Promise<WealthPOSummaryResponse | null> => {
    return get(`/holistic-advice/profiles/${profileId}/wealthpo/strategy/summary/${selectedState}`)
        .then(extractNullableBody);
}

export const wealthPOApiClient = {
    getStateEstateTransferRatesReferenceData,
    getGRATRatesReferenceData,
    getPayoutPercentages,
    getEstimatedImpactForSOR,
    getEstimatedImpactForAnnualGifting,
    getEstimatedImpactForGiftToGrantorTrust,
    getEstimatedImpactForGRAT,
    saveStateOfResidencyStrategy,
    editStateOfResidencyStrategy,
    editStrategyEnabledStatus,
    createAnnualGiftingStrategy,
    editAnnualGiftingStrategy,
    createGiftToGrantorTrustStrategy,
    editGiftToGrantorTrustStrategy,
    createGRATStrategy,
    editGRATStrategy,
    deleteStrategy,
    deleteStateOfResidencyStrategy,
    getWealthPOSummary,
    getWealthPOSummaryForSelectedState
}


